import type { SchrijvenVariantContext } from "@a2/core";
import { useT } from "../../lib/use-t";

interface WijkkrantShellProps {
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
}

/**
 * Wijkkrant composer — printed-bulletin framing. The masthead conveys "this is
 * a notice in a community newspaper" so the student writes in that register.
 * Body is a single textarea; no greeting/closing. Word count lives in the
 * runner's bottom-bar meter, so there's no per-shell counter row.
 */
export function WijkkrantShell({
  variant: _variant,
  value,
  onChange,
  disabled = false,
}: WijkkrantShellProps) {
  const t = useT();

  return (
    <article className="flex flex-col min-h-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
      <header className="px-5 py-4 border-b border-[var(--border)] bg-[var(--panel-soft)] flex items-start gap-3">
        <div>
          <h2 className="font-mono-badge text-base font-bold uppercase tracking-[0.2em] text-[var(--ink)]">
            De Wijkkrant
          </h2>
          <p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)] mt-1">
            Kleine advertenties · Mededelingen uit de buurt
          </p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono-badge text-xs text-[var(--ink-faint)] shrink-0">
          <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--pass)]" aria-hidden />
          {t("schrijven.runner.saved")}
        </span>
      </header>

      <div className="flex-1 min-h-0 px-5 py-5 overflow-auto">
        <div className="border-l-2 border-[var(--border)] pl-4 h-full">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            rows={11}
            placeholder={t("schrijven.runner.wijkkrantPlaceholder")}
            className="w-full bg-transparent border-0 px-0 py-0 text-[16px] text-[var(--ink)] leading-relaxed placeholder:text-[var(--ink-faint)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-4"
            aria-label={t("schrijven.runner.wijkkrantLabel")}
          />
        </div>
      </div>
    </article>
  );
}
